import { useCallback } from 'react';

import type {
  ApprovalAction,
  ApprovalData,
  ApprovalType,
} from 'ui-product-approvals-common';

import type {
  AppHubUserRecord,
  AppTeamRecord,
  BreezeAgentRecord,
} from '../types/CrmSearchUser';
import { Metrics } from '../utils/metrics';
import { captureException } from '../utils/sentryUtils';
import { assertApprovalDataResolved } from '../utils/assertApprovalDataResolved';
import { toApprovalData } from '../utils/approvalResponseAdapters';
import { useApproveApprovalStepMutation } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { GET_LATEST_APPROVAL_QUERY } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.dfc.new';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from 'ui-product-approvals-common/constants/approvalTypeIds';

interface UseApproveApprovalStepArgs {
  approvalType: ApprovalType;
  approvalAction: ApprovalAction;
  approvableObjectId: number | undefined;
  usersRecord: AppHubUserRecord;
  agentsRecord: BreezeAgentRecord;
  teamsRecord: AppTeamRecord;
  resolvedData: ApprovalData;
}

export const useApproveApprovalStep = ({
  approvalType,
  approvalAction,
  approvableObjectId,
  usersRecord,
  agentsRecord,
  teamsRecord,
  resolvedData,
}: UseApproveApprovalStepArgs) => {
  const [approveApprovalStepMutation] = useApproveApprovalStepMutation();

  const approveStep = useCallback(
    (approvalStepId: number, note: string): Promise<ApprovalData> => {
      const validated = assertApprovalDataResolved(
        'useApproveApprovalStep',
        resolvedData,
        approvableObjectId
      );
      return approveApprovalStepMutation({
        variables: { approvalStepId, note },
        update: (cache, { data: mutationData }) => {
          const approvalData = mutationData?.result?.approval;
          if (approvalData) {
            cache.writeQuery({
              query: GET_LATEST_APPROVAL_QUERY,
              variables: {
                approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
                approvalAction,
                approvableObjectId: validated.approvableObjectId,
              },
              data: { result: { approval: approvalData } },
            });
          }
        },
      })
        .then((result) => {
          Metrics.counter('request-approved').increment();
          const approval = result?.data?.result?.approval;
          if (approval) {
            return toApprovalData(
              approval,
              usersRecord,
              agentsRecord,
              approvalType,
              approvalAction,
              teamsRecord
            );
          } else {
            throw new Error(
              'approveApprovalStep succeeded but returned no approval data'
            );
          }
        })
        .catch((error) => {
          captureException(error, {
            extra: {
              fingerprint: 'useApprovalData-approveStep',
              approvalType,
              approvalAction,
              approvalStepId,
            },
          });
          throw error;
        });
    },
    [
      approveApprovalStepMutation,
      resolvedData,
      usersRecord,
      agentsRecord,
      teamsRecord,
      approvalType,
      approvalAction,
      approvableObjectId,
    ]
  );

  return approveStep;
};
